
import React, { useState } from 'react';
import { KeyContact } from '@/types/lead';
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useToast } from '@/hooks/use-toast';
import { addKeyContact, updateKeyContact, deleteKeyContact } from '@/services/leads/keyContactService';
import { useIsMobile } from '@/hooks/use-mobile';

// Import our new components
import ContactCard from './contacts/ContactCard';
import EmptyContactsState from './contacts/EmptyContactsState';
import AddContactDialog from './contacts/AddContactDialog';
import EditContactDialog from './contacts/EditContactDialog';
import DeleteContactDialog from './contacts/DeleteContactDialog';
import { ContactFormData } from './contacts/ContactForm';

interface KeyContactsTabContentProps {
  leadId: string;
  contacts: KeyContact[];
  onContactsUpdated: () => void;
}

const KeyContactsTabContent: React.FC<KeyContactsTabContentProps> = ({ leadId, contacts, onContactsUpdated }) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [isEditContactDialogOpen, setIsEditContactDialogOpen] = useState(false);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<KeyContact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    position: '',
    email: '',
    phone: '',
    notes: '',
    isPrimary: false
  });

  const handleAddContact = async () => {
    try {
      if (!formData.name) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Contact name is required"
        });
        return;
      }

      await addKeyContact(leadId, {
        name: formData.name,
        position: formData.position || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        isPrimary: formData.isPrimary
      });

      toast({
        title: "Contact Added",
        description: "The contact has been added successfully"
      });

      // Reset form data
      setFormData({
        name: '',
        position: '',
        email: '',
        phone: '',
        notes: '',
        isPrimary: false
      });

      setIsAddContactDialogOpen(false);
      onContactsUpdated();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add contact"
      });
    }
  };

  const handleEditContact = async () => {
    try {
      if (!selectedContact) return;
      if (!formData.name) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Contact name is required"
        });
        return;
      }

      await updateKeyContact(selectedContact.id, {
        name: formData.name,
        position: formData.position || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        isPrimary: formData.isPrimary
      });

      toast({
        title: "Contact Updated",
        description: "The contact has been updated successfully"
      });

      setIsEditContactDialogOpen(false);
      onContactsUpdated();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update contact"
      });
    }
  };

  const handleDeleteContact = async () => {
    try {
      if (!selectedContact) return;

      await deleteKeyContact(selectedContact.id);

      toast({
        title: "Contact Deleted",
        description: "The contact has been deleted successfully"
      });

      setIsDeleteContactDialogOpen(false);
      onContactsUpdated();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete contact"
      });
    }
  };

  const openAddContactDialog = () => {
    setFormData({
      name: '',
      position: '',
      email: '',
      phone: '',
      notes: '',
      isPrimary: false
    });
    setIsAddContactDialogOpen(true);
  };

  const openEditContactDialog = (contact: KeyContact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      position: contact.position || '',
      email: contact.email || '',
      phone: contact.phone || '',
      notes: contact.notes || '',
      isPrimary: contact.isPrimary
    });
    setIsEditContactDialogOpen(true);
  };

  const openDeleteContactDialog = (contact: KeyContact) => {
    setSelectedContact(contact);
    setIsDeleteContactDialogOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-4 md:px-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-5 border-b">
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Key Contacts</h2>
            {contacts.length > 0 && (
              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold bg-primary/10 text-primary">
                {contacts.length}
              </span>
            )}
          </div>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            Manage key contacts and decision makers for this lead. Keep track of important people and their information in one place.
          </p>
        </div>
        <Button 
          onClick={openAddContactDialog}
          size="lg"
          className="w-full sm:w-auto h-10 px-5 font-semibold shadow-sm hover:shadow-md transition-all"
        >
          Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <EmptyContactsState onAddContact={openAddContactDialog} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5 animate-fade-in">
          {contacts.map((contact, index) => (
            <div 
              key={contact.id}
              className="animate-fade-in"
              style={{ 
                animationDelay: `${index * 50}ms`,
                animationFillMode: 'backwards'
              }}
            >
              <ContactCard 
                contact={contact}
                onEdit={openEditContactDialog}
                onDelete={openDeleteContactDialog}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add Contact Dialog */}
      <Dialog open={isAddContactDialogOpen} onOpenChange={setIsAddContactDialogOpen}>
        <AddContactDialog
          isMobile={isMobile}
          formData={formData}
          setFormData={setFormData}
          onAddContact={handleAddContact}
        />
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={isEditContactDialogOpen} onOpenChange={setIsEditContactDialogOpen}>
        <EditContactDialog
          isMobile={isMobile}
          formData={formData}
          setFormData={setFormData}
          onEditContact={handleEditContact}
        />
      </Dialog>

      {/* Delete Contact Dialog */}
      <Dialog open={isDeleteContactDialogOpen} onOpenChange={setIsDeleteContactDialogOpen}>
        <DeleteContactDialog
          isMobile={isMobile}
          onDeleteContact={handleDeleteContact}
        />
      </Dialog>
    </div>
  );
};

export default KeyContactsTabContent;
